import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IceCream, CheckCircle, XCircle, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { cajaAPI } from '@/services/api';

const CajaPage = () => {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingRedemption, setPendingRedemption] = useState(null);
  const [confirmed, setConfirmed] = useState(false);

  const handleVerifyCode = async () => {
    if (code.length !== 4) {
      toast.error('Ingresa un código de 4 dígitos');
      return;
    }

    setLoading(true);
    try {
      const response = await cajaAPI.verifyCode(code);
      setPendingRedemption(response.data.pending);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Código inválido o expirado');
      setPendingRedemption(null);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    setLoading(true);
    try {
      const response = await cajaAPI.confirmRedemption(code);
      toast.success(response.data.message);
      setConfirmed(true);
      setTimeout(() => {
        setCode('');
        setPendingRedemption(null);
        setConfirmed(false);
      }, 3000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al confirmar');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setCode('');
    setPendingRedemption(null);
    setConfirmed(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-100 to-amber-50">
      {/* Header */}
      <header className="bg-white shadow-md">
        <div className="max-w-lg mx-auto px-4 py-4 flex items-center gap-4">
          <button 
            onClick={() => navigate('/')}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-rose-500 flex items-center justify-center">
              <IceCream className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-nunito font-bold text-lg text-gray-900">Caja</h1>
              <p className="text-xs text-gray-500">Confirmar canjes</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-8">
        {confirmed ? (
          /* Success State */
          <Card className="border-2 border-green-300 rounded-3xl">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-10 h-10 text-green-500" />
              </div>
              <h2 className="font-nunito font-bold text-2xl text-green-600 mb-2">
                ¡Canje Confirmado!
              </h2>
              <p className="text-gray-600">
                {pendingRedemption?.reward_name}
              </p>
              <p className="text-sm text-gray-500 mt-2">
                Cliente: {pendingRedemption?.customer_name}
              </p>
            </CardContent>
          </Card>
        ) : pendingRedemption ? (
          /* Confirmation State */
          <Card className="border-2 border-amber-300 rounded-3xl">
            <CardContent className="p-6">
              <h2 className="font-nunito font-bold text-xl text-center text-gray-900 mb-6">
                Confirmar Canje
              </h2>
              
              <div className="bg-amber-50 rounded-2xl p-4 mb-6">
                <div className="text-center mb-4">
                  <p className="text-sm text-gray-500">Cliente</p>
                  <p className="font-nunito font-bold text-lg text-gray-900">
                    {pendingRedemption.customer_name}
                  </p>
                  <p className="text-sm text-gray-500">
                    {pendingRedemption.customer_phone}
                  </p>
                </div>
                
                <div className="border-t border-amber-200 pt-4 text-center">
                  <p className="text-sm text-gray-500">Premio a canjear</p>
                  <p className="font-nunito font-bold text-lg text-amber-600">
                    {pendingRedemption.reward_name}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    Costo: {pendingRedemption.cost} {pendingRedemption.currency_type === 'stickers' ? 'stickers' : 'monedas'}
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={handleCancel}
                  variant="outline"
                  className="flex-1 py-6 rounded-xl border-2 border-gray-300"
                  disabled={loading}
                >
                  <XCircle className="w-5 h-5 mr-2" />
                  Cancelar
                </Button>
                <Button
                  onClick={handleConfirm}
                  className="flex-1 py-6 rounded-xl bg-green-500 hover:bg-green-600 text-white"
                  disabled={loading}
                >
                  <CheckCircle className="w-5 h-5 mr-2" />
                  {loading ? 'Confirmando...' : 'Confirmar'}
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          /* Input State */
          <Card className="border-2 border-rose-200 rounded-3xl">
            <CardContent className="p-6">
              <h2 className="font-nunito font-bold text-xl text-center text-gray-900 mb-2">
                Código de Canje
              </h2>
              <p className="text-center text-gray-500 text-sm mb-6">
                Ingresa el código de 4 dígitos que muestra el cliente
              </p>

              <Input
                type="text"
                inputMode="numeric"
                maxLength={4}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                placeholder="0000"
                className="text-center text-4xl font-bold py-8 rounded-2xl border-2 border-rose-200 focus:border-rose-500 tracking-[0.5em]"
                data-testid="caja-code-input"
              />

              <Button
                onClick={handleVerifyCode}
                disabled={code.length !== 4 || loading}
                className="w-full mt-6 py-6 rounded-xl bg-rose-500 hover:bg-rose-600 text-white font-bold text-lg"
                data-testid="caja-verify-btn"
              >
                {loading ? 'Verificando...' : 'Verificar Código'}
              </Button>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default CajaPage;
